import theme from 'config/theme'
import { lighten } from 'lib/styleUtils'
import { comfortModel } from 'lib/thermalComfortTool/comfortModels'
import { util } from 'lib/thermalComfortTool/util'
import { Temperature, Velocity } from '~/store/units/types'
import { FormattedUnitData, UnformattedUnitData } from '~/store/units/types/unit'

const colorMap: Partial<Record<string, string>> = {
  Cold: theme.colors.pmv.cold,
  Cool: lighten(theme.colors.pmv.cold, 0.2),
  'Slightly Cool': lighten(theme.colors.pmv.cold, 0.4),
  Neutral: theme.colors.light.subdued,
  'Slightly Warm': lighten(theme.colors.pmv.hot, 0.4),
  Warm: lighten(theme.colors.pmv.hot, 0.2),
  Hot: theme.colors.pmv.hot,
}

export const getSensationColor = (sensation: string) => colorMap[sensation] ?? theme.colors.light.subdued

/**
 * Calculates Thermal Comfort via the CBE Tool. Input data is in metric,
 * but will be converted if imperial values are passed in. Arguments such
 * as humidity, metabolicRate, and clothingLevel generally come from static
 * default facility data.
 */
interface ThermalComfortProps {
  meanAirTemp: Temperature<FormattedUnitData | UnformattedUnitData>
  meanRadiantTemp?: Temperature<FormattedUnitData | UnformattedUnitData>
  airVelocity: Velocity<FormattedUnitData | UnformattedUnitData>
  humidity: number
  metabolicRate: number
  clothingType: number
  externalWork?: number
  occupantAirSpeed?: boolean
}

interface ThermalComfortResult {
  pmv: number
  ppd: number
  set: number
  sensation: string
  coolingEffect: number
}

export const getThermalComfort = ({
  meanAirTemp,
  meanRadiantTemp = meanAirTemp,
  airVelocity,
  humidity,
  metabolicRate,
  clothingType,
  externalWork = 0,
  occupantAirSpeed = false,
}: ThermalComfortProps): ThermalComfortResult => {
  const ta = meanAirTemp.metric()
  const tr = meanRadiantTemp.metric()
  const vel = airVelocity.metric()
  const sanitizedHumidity = Math.max(Math.min(100, humidity), 0)
  const { ppd, pmv, set, cooling_effect } = comfortModel.pmvElevatedAirspeed(
    ta,
    tr,
    vel,
    sanitizedHumidity,
    metabolicRate,
    clothingType,
    externalWork,
    occupantAirSpeed
  )
  const isValidPmv = !isNaN(pmv)
  const sanitizedPmv = isValidPmv ? pmv : 0
  if (!isValidPmv) console.warn('Invalid parameters were entered into thermal comfort tool.')
  const sensation = util.getSensation(sanitizedPmv)
  return {
    pmv: sanitizedPmv,
    ppd,
    set,
    sensation,
    coolingEffect: cooling_effect,
  }
}
